import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Camera,
  SwitchCamera,
  X,
  Package,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Keyboard,
  ScanBarcode,
  Loader2,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const InwardScanner = ({ 
  isOpen, 
  onClose, 
  sessionId, 
  hubId, 
  onScanSuccess,
  sessionData 
}) => {
  const [scanning, setScanning] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [cameraFacing, setCameraFacing] = useState("environment"); // environment = back, user = front
  const [lastScanResult, setLastScanResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isProcessingRef = useRef(false);
  const recentScansRef = useRef(new Set());
  const successfulScansRef = useRef({}); // code → {location_name, sublocation_name}

  // Build a lookup map for quick shipment finding
  const shipmentLookup = useRef({});
  
  useEffect(() => {
    if (sessionData?.assignments) {
      const lookup = {};
      sessionData.assignments.forEach(assignment => {
        assignment.sublocation_assignments?.forEach(sub => {
          // Index by package_id, shipment_code, reference_id, lbn
          if (sub.package_id) lookup[sub.package_id] = { ...sub, assignment };
          if (sub.shipment_code) lookup[sub.shipment_code] = { ...sub, assignment };
          if (sub.reference_id) lookup[sub.reference_id] = { ...sub, assignment };
          if (sub.lbn) lookup[sub.lbn] = { ...sub, assignment };
        });
      });
      shipmentLookup.current = lookup;
    }
  }, [sessionData]);

  useEffect(() => {
    if (isOpen && !manualMode) {
      initializeScanner();
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen, manualMode]);

  const initializeScanner = async () => {
    try {
      // Get available cameras
      const devices = await Html5Qrcode.getCameras();
      setAvailableCameras(devices);
      
      if (devices.length === 0) {
        toast.error("No camera found on this device");
        setManualMode(true);
        return;
      }

      // Prefer back camera on mobile
      let preferredCamera = devices.find(d => 
        d.label.toLowerCase().includes("back") || 
        d.label.toLowerCase().includes("environment")
      );
      
      if (!preferredCamera) {
        preferredCamera = devices[0];
      }
      
      setSelectedCameraId(preferredCamera.id);
      await startScanner(preferredCamera.id);
    } catch (err) {
      console.error("Camera initialization error:", err);
      toast.error("Failed to access camera. Please allow camera permissions.");
      setManualMode(true);
    }
  };

  const startScanner = async (cameraId) => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {
        // Ignore stop errors
      }
    }

    try {
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanDetected,
        (errorMessage) => {
          // QR code detection failure - this is normal when no code is in view
        }
      );
      
      setScanning(true);
    } catch (err) {
      console.error("Scanner start error:", err);
      toast.error("Failed to start scanner");
      setManualMode(true);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (e) {
        // Ignore stop errors
      }
    }
    setScanning(false);
  };

  const pauseScanner = () => {
    if (html5QrCodeRef.current) {
      try { html5QrCodeRef.current.pause(true); } catch (e) {}
    }
  };

  const resumeScanner = () => {
    if (html5QrCodeRef.current) {
      try { html5QrCodeRef.current.resume(); } catch (e) {}
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) {
      toast.info("Only one camera available");
      return;
    }

    const currentIdx = availableCameras.findIndex(c => c.id === selectedCameraId);
    const nextIdx = (currentIdx + 1) % availableCameras.length;
    const nextCamera = availableCameras[nextIdx];
    
    setSelectedCameraId(nextCamera.id);
    await stopScanner();
    await startScanner(nextCamera.id);
    
    toast.info(`Switched to ${nextCamera.label || "Camera " + (nextIdx + 1)}`);
  };

  const onScanDetected = async (decodedText) => {
    // Immediate ref guard
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    // Pause scanner immediately so it can't fire again
    pauseScanner();
    
    // Play beep sound
    playBeep();
    
    await processScannedCode(decodedText);

    // Resume scanner after a short delay to avoid re-reading the same code
    setTimeout(() => {
      isProcessingRef.current = false;
      resumeScanner();
    }, 2000);
  };

  const playBeep = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 1000;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);
    } catch (e) {
      // Audio not supported
    }
  };

  const processScannedCode = async (code) => {
    setIsProcessing(true);
    setLastScanResult(null);

    // First check locally if shipment exists in session
    const localMatch = shipmentLookup.current[code];
    
    if (!localMatch) {
      setLastScanResult({
        success: false,
        error: "WRONG_PARCEL",
        message: `Package "${code}" not found in this session`,
        code,
      });
      playErrorBeep();
      setIsProcessing(false);
      return;
    }

    // Check if already scanned locally
    const inwardedShipments = new Set(
      (sessionData?.inwarded_shipments || []).map(s => s.shipment_id || s)
    );
    if (inwardedShipments.has(localMatch.shipment_id)) {
      // Use stored data from previous successful scan, fallback to lookup
      const prevScan = successfulScansRef.current[code] || successfulScansRef.current[localMatch.shipment_id];
      setLastScanResult({
        success: false,
        error: "ALREADY_SCANNED",
        message: `Package "${code}" has already been scanned`,
        code,
        location_name: prevScan?.location_name || localMatch.assignment?.location_name,
        sublocation_name: prevScan?.sublocation_name || localMatch.sublocation_name,
      });
      playErrorBeep();
      setIsProcessing(false);
      return;
    }

    // Call backend to process inward
    try {
      const res = await axios.post(`${API}/inward/${hubId}/sessions/${sessionId}/scan`, {
        scan_code: code,
      });

      // Store successful scan info for future "already scanned" lookups
      successfulScansRef.current[code] = {
        location_name: res.data.location_name,
        sublocation_name: res.data.sublocation_name,
      };
      if (res.data.shipment_id) {
        successfulScansRef.current[res.data.shipment_id] = {
          location_name: res.data.location_name,
          sublocation_name: res.data.sublocation_name,
        };
      }

      setLastScanResult({
        success: true,
        message: "Package inwarded successfully!",
        code,
        shipment_id: res.data.shipment_id,
        location_name: res.data.location_name,
        sublocation_name: res.data.sublocation_name,
        trip_number: res.data.trip_number,
      });

      playSuccessBeep();
      
      // Notify parent to refresh data
      if (onScanSuccess) {
        onScanSuccess(res.data);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || "Scan failed";
      
      let errorType = "UNKNOWN";
      if (errorMsg.includes("already inwarded")) {
        errorType = "ALREADY_SCANNED";
      } else if (errorMsg.includes("does not belong")) {
        errorType = "WRONG_PARCEL";
      }

      // For "already inwarded" from backend, try to get location from stored data
      const prevScan = successfulScansRef.current[code];
      setLastScanResult({
        success: false,
        error: errorType,
        message: errorMsg,
        code,
        location_name: prevScan?.location_name,
        sublocation_name: prevScan?.sublocation_name,
      });
      playErrorBeep();
    }

    setIsProcessing(false);
  };

  const playSuccessBeep = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.frequency.value = 1200;
      }, 100);
      oscillator.stop(audioCtx.currentTime + 0.2);
    } catch (e) {}
  };

  const playErrorBeep = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 300;
      oscillator.type = "square";
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (e) {}
  };

  const manualAutoSubmitRef = useRef(null);

  const handleManualSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!manualInput.trim() || isProcessing) return;
    
    // Clear any pending auto-submit
    if (manualAutoSubmitRef.current) clearTimeout(manualAutoSubmitRef.current);
    
    const code = manualInput.trim();
    setManualInput("");
    playBeep();
    await processScannedCode(code);
  };

  const handleManualInputChange = (val) => {
    setManualInput(val);
    // Auto-submit after 500ms of no typing (barcode scanners type fast then stop)
    if (manualAutoSubmitRef.current) clearTimeout(manualAutoSubmitRef.current);
    if (val.trim().length >= 3) {
      manualAutoSubmitRef.current = setTimeout(async () => {
        if (val.trim() && !isProcessing) {
          const code = val.trim();
          setManualInput("");
          playBeep();
          await processScannedCode(code);
        }
      }, 500);
    }
  };

  const handleClose = async () => {
    await stopScanner();
    setLastScanResult(null);
    setManualInput("");
    isProcessingRef.current = false;
    recentScansRef.current.clear();
    successfulScansRef.current = {};
    if (manualAutoSubmitRef.current) clearTimeout(manualAutoSubmitRef.current);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="p-4 bg-emerald-600 text-white">
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ScanBarcode className="w-5 h-5" />
              Inward Scanner
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (manualMode) {
                    setManualMode(false);
                    initializeScanner();
                  } else {
                    stopScanner();
                    setManualMode(true);
                  }
                }}
                className="text-white hover:bg-emerald-700"
              >
                {manualMode ? (
                  <>
                    <Camera className="w-4 h-4 mr-1" /> Camera
                  </>
                ) : (
                  <>
                    <Keyboard className="w-4 h-4 mr-1" /> Manual
                  </>
                )}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Camera View */}
          {!manualMode && (
            <div className="relative">
              <div 
                id="qr-reader" 
                ref={scannerRef}
                className="w-full rounded-lg overflow-hidden bg-black min-h-[300px]"
              />
              
              {/* Camera switch button */}
              {availableCameras.length > 1 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={switchCamera}
                  className="absolute bottom-4 right-4 bg-white/90 hover:bg-white"
                  data-testid="switch-camera-btn"
                >
                  <SwitchCamera className="w-4 h-4 mr-1" /> Switch
                </Button>
              )}

              {/* Scanning indicator */}
              {scanning && !isProcessing && (
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium">Scanning...</span>
                </div>
              )}

              {isProcessing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>
          )}

          {/* Manual Entry */}
          {manualMode && (
            <form onSubmit={handleManualSubmit} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Enter Package ID or Reference ID
                </label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={manualInput}
                    onChange={(e) => handleManualInputChange(e.target.value)}
                    placeholder="Scan or type Package ID / Reference ID"
                    className="flex-1"
                    autoFocus
                    data-testid="manual-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={!manualInput.trim() || isProcessing}
                    style={{ backgroundColor: "#1B8C3D" }}
                    data-testid="manual-submit-btn"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Inward"
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                You can scan Package ID, Shipment ID, Reference ID, or LBN
              </p>
            </form>
          )}

          {/* Last Scan Result */}
          {lastScanResult && (
            <Card className={`border-2 ${
              lastScanResult.success 
                ? "border-emerald-500 bg-emerald-50" 
                : "border-red-500 bg-red-50"
            }`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {lastScanResult.success ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  )}
                  
                  <div className="flex-1">
                    <p className={`font-semibold ${
                      lastScanResult.success ? "text-emerald-800" : "text-red-800"
                    }`}>
                      {lastScanResult.message}
                    </p>
                    
                    {lastScanResult.code && (
                      <p className="text-sm text-slate-600 mt-1 font-mono">
                        Code: {lastScanResult.code}
                      </p>
                    )}
                    
                    {lastScanResult.success && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">
                            <span className="text-slate-500">Location:</span>{" "}
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {lastScanResult.location_name}
                            </Badge>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">
                            <span className="text-slate-500">Slot:</span>{" "}
                            <Badge className="bg-blue-100 text-blue-700">
                              {lastScanResult.sublocation_name}
                            </Badge>
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!lastScanResult.success && lastScanResult.error === "ALREADY_SCANNED" && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">
                            <span className="text-slate-500">Was at:</span>{" "}
                            <Badge variant="outline">
                              {lastScanResult.location_name} / {lastScanResult.sublocation_name}
                            </Badge>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          {sessionData && (
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <span className="text-sm text-slate-600">Session Progress</span>
              <span className="font-semibold text-emerald-700">
                {sessionData.inwarded_shipments?.length || 0} / {sessionData.total_shipments || 0} Inwarded
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t flex justify-end">
          <Button variant="outline" onClick={handleClose} data-testid="close-scanner-btn">
            <X className="w-4 h-4 mr-2" /> Close Scanner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InwardScanner;
